import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useNotifications } from '../contexts/NotificationContext';
import api from '../services/api';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import ConfirmModal from '../components/ConfirmModal';
import { Users as UsersIcon, Plus, Search, Edit, Trash2, Shield, User, Fingerprint, Key, Eye, EyeOff } from 'lucide-react';
import FingerprintScanner from '../components/FingerprintScanner';
import UserFormModal from '../components/UserFormModal';
import './Users.css';

const Users = () => {
  useMarkAsReadOnMount(['user']);

  const { isAdmin } = useAuth();
  const { hasPermission } = usePermissions();
  const { success, error } = useNotifications();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState({});
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollingUser, setEnrollingUser] = useState(null);
  const [enrollScanStatus, setEnrollScanStatus] = useState('ready');
  const [enrollError, setEnrollError] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);


  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const getUserProfilePhoto = (user) => {
    const key = `profile_photo_${user?.id || user?.userId || ''}`;
    return localStorage.getItem(key) || '';
  };

  const fetchUsers = useCallback(async () => {
    try {
      const users = await api.get('/users');
      const normalizedUsers = normalizeArray(users);
      console.log('Fetched users with departments:', normalizedUsers);
      setUsers(normalizedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const depts = await api.get('/departments');
      setDepartments(normalizeArray(depts));
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);

  const fetchEnrollmentStatuses = useCallback(async (userList) => {
    if (!userList?.length) return;
    try {
      const enrolled = await api.get('/biometric/enrolled-users');
      const list = Array.isArray(enrolled) ? enrolled : (enrolled?.data ?? []);
      const map = {};
      list.forEach((e) => { map[e.userId] = true; });
      setEnrollmentStatus(map);
    } catch { /* biometric may not be enabled */ }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
    const timer = setInterval(fetchUsers, 30000);
    return () => clearInterval(timer);
  }, [fetchUsers, fetchDepartments]);

  useEffect(() => {
    if (users.length) fetchEnrollmentStatuses(users);
  }, [users, fetchEnrollmentStatuses]);

  const openEnrollModal = (user) => {
    setEnrollingUser(user);
    setEnrollScanStatus('ready');
    setEnrollError(null);
    setShowEnrollModal(true);
  };

  const handleEnroll = async () => {
    if (!enrollingUser) return;
    setEnrollScanStatus('scanning');
    setEnrollError(null);
    try {
      // TODO: Replace null with actual captured template from device SDK
      // e.g.: const { templateData } = await deviceSdk.capture();
      const templateData = null;
      if (!templateData) {
        throw new Error('DEVICE_NOT_CONNECTED: Connect the fingerprint reader and implement the SDK capture in useBiometric.js');
      }
      await api.post(`/biometric/enroll/${enrollingUser.id}`, { templateData, quality: 0 });
      setEnrollmentStatus((prev) => ({ ...prev, [enrollingUser.id]: true }));
      setEnrollScanStatus('success');
      success(`Fingerprint enrolled for ${enrollingUser.firstName || enrollingUser.email}`);
      setTimeout(() => setShowEnrollModal(false), 1500);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Enrollment failed';
      setEnrollError(msg);
      setEnrollScanStatus('error');
    }
  };

  const handleUnenroll = async (userId) => {
    try {
      await api.delete(`/biometric/unenroll/${userId}`);
      setEnrollmentStatus((prev) => { const n = { ...prev }; delete n[userId]; return n; });
      success('Fingerprint enrollment removed');
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to remove enrollment');
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const openEditModal = async (user) => {
    try {
      const freshUser = await api.get(`/users/${user.id}`);
      setEditingUser(freshUser || user);
      setShowModal(true);
    } catch (err) {
      error('Unable to load latest user data for edit.');
    }
  };

  const closeUserModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  // Errors are rethrown so the form can display them and stay open
  const handleSaveUser = async (payload) => {
    if (editingUser) {
      await api.patch(`/users/${editingUser.id}`, payload);
    } else {
      await api.post('/users', payload);
    }

    await fetchUsers();
    success(`User ${editingUser ? 'updated' : 'created'} successfully!`);
    closeUserModal();
  };

  const handleDelete = async (id) => {
    const user = users.find(u => u.id === id);
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/users/${userToDelete.id}`);
      await fetchUsers();
      const userName = `${userToDelete.firstName} ${userToDelete.lastName} (${userToDelete.email})`;
      success(`User ${userName} deleted successfully!`);
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      error('Error deleting user: ' + errorMessage);
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const openResetPasswordModal = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordText(false);
    setShowPasswordModal(true);
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!userToResetPassword) return;

    if (newPassword.length < 6) {
      error('Password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      error('Passwords do not match.');
      return;
    }

    setResettingPassword(true);
    try {
      await api.patch(`/users/${userToResetPassword.id}/reset-password`, { newPassword });
      const userName = `${userToResetPassword.firstName || ''} ${userToResetPassword.lastName || ''}`.trim() || userToResetPassword.email;
      success(`Password for ${userName} reset successfully!`);
      setShowPasswordModal(false);
      setUserToResetPassword(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error resetting password:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to reset password';
      error(msg);
    } finally {
      setResettingPassword(false);
    }
  };


  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    // Search filter
    const matchesSearch = !searchTerm || 
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Role filter
    const matchesRole = !roleFilter || user.role?.toLowerCase() === roleFilter.toLowerCase();
    
    // Department filter
    const matchesDepartment = !departmentFilter
      || user.departmentId?.toString() === departmentFilter
      || user.department?.id?.toString() === departmentFilter;
    
    // Status filter
    const matchesStatus = !statusFilter || 
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesRole && matchesDepartment && matchesStatus;
  }) : [];

  if (loading) {
    return <div className="page-loading">Loading users...</div>;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><UsersIcon size={24} /> User Management</h1>
        {hasPermission('users') && <button className="btn btn-primary btn-sm" onClick={openCreateModal}><Plus size={14} /> Add User</button>}
      </div>

      <div className="users-filters">
        <div className="search-bar">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-controls">
          <select 
            value={roleFilter} 
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
            <option value="intern">Intern</option>
          </select>
          
          <select 
            value={departmentFilter} 
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Departments</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
          
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="list-meta">
          Showing <strong>{filteredUsers.length}</strong> user{filteredUsers.length === 1 ? '' : 's'}
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Matricule</th>
              <th>CIN</th>
              <th>Contract</th>
              <th>Role</th>
              <th>Department</th>
              <th>Biometric</th>
              {hasPermission('users') && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {getUserProfilePhoto(user) ? (
                        <img src={getUserProfilePhoto(user)} alt="Profile" className="user-avatar-image" />
                      ) : (
                        user.firstName?.[0] || user.email?.[0]?.toUpperCase() || '?'
                      )}
                    </div>
                    <span>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Unnamed User'}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td><code>{user.matricule || '-'}</code></td>
                <td>{user.cin || '-'}</td>
                <td>
                  {user.contractType === 'INTERNSHIP_SUMMER' ? 'Stage (Summer)' :
                   user.contractType === 'INTERNSHIP_END_OF_STUDY' ? 'Stage (PFE / End of Study)' :
                   user.contractType === 'INTERNSHIP' ? 'Stage' :
                   user.contractType || '-'}
                </td>
                <td>
                  <span className={`role-badge ${(user.role || '').toLowerCase()}`}>
                    {user.role === 'ADMIN' && <Shield size={12} />}
                    {user.role === 'EMPLOYEE' && <User size={12} />}
                    {user.role}
                  </span>
                </td>
                <td>{user.department?.name || '-'}</td>
                <td>
                  {enrollmentStatus[user.id] ? (
                    <span className="biometric-badge enrolled">
                      <Fingerprint size={12} /> Enrolled
                    </span>
                  ) : (
                    <span className="biometric-badge not-enrolled">
                      Not enrolled
                    </span>
                  )}
                </td>
                {hasPermission('users') && (
                  <td className="actions-cell">
                    <div className="actions">
                      {hasPermission('users') && (
                        <button
                          className="btn-icon"
                          title="Edit User"
                          onClick={() => openEditModal(user)}
                        >
                          <Edit size={14} />
                        </button>
                      )}
                      {hasPermission('users') && (
                        <button
                          className="btn-icon"
                          title="Reset Password"
                          onClick={() => openResetPasswordModal(user)}
                        >
                          <Key size={14} />
                        </button>
                      )}
                      {hasPermission('users') && (
                        <button
                          className="btn-icon"
                          title={enrollmentStatus[user.id] ? 'Re-enroll fingerprint' : 'Enroll fingerprint'}
                          onClick={() => openEnrollModal(user)}
                        >
                          <Fingerprint size={14} />
                        </button>
                      )}
                      {hasPermission('users') && enrollmentStatus[user.id] && (
                        <button
                          className="btn-icon delete"
                          title="Remove fingerprint enrollment"
                          onClick={() => handleUnenroll(user.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      {hasPermission('users') && String(user.role || '').toUpperCase() !== 'ADMIN' && (
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDelete(user.id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {!filteredUsers.length && (
              <tr>
                <td colSpan={hasPermission('users') ? 9 : 8} className="empty-row">
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <UserFormModal
          key={editingUser?.id || 'new'}
          user={editingUser}
          users={users}
          departments={departments}
          canAssignAdmin={isAdmin()}
          onSave={handleSaveUser}
          onClose={closeUserModal}
        />
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${userToDelete ? `${userToDelete.firstName} ${userToDelete.lastName} (${userToDelete.email})` : 'this user'}? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {showEnrollModal && enrollingUser && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Fingerprint size={20} />
              {enrollmentStatus[enrollingUser.id] ? 'Re-enroll' : 'Enroll'} Fingerprint
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Employee: <strong style={{ color: 'var(--text-primary)' }}>
                {`${enrollingUser.firstName || ''} ${enrollingUser.lastName || ''}`.trim() || enrollingUser.email}
              </strong>
            </p>
            <FingerprintScanner
              status={enrollScanStatus}
              error={enrollError}
              onScan={handleEnroll}
              disabled={enrollScanStatus === 'scanning' || enrollScanStatus === 'success'}
              label={
                enrollScanStatus === 'ready'
                  ? 'Ask the employee to place their finger on the reader, then click Scan.'
                  : undefined
              }
            />
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEnrollModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && userToResetPassword && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Key size={20} style={{ color: 'var(--primary, #3b82f6)' }} />
              Reset User Password
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Set a new password for <strong style={{ color: 'var(--text-primary)' }}>
                {`${userToResetPassword.firstName || ''} ${userToResetPassword.lastName || ''}`.trim() || userToResetPassword.email}
              </strong> ({userToResetPassword.email}).
            </p>
            <form onSubmit={handleResetPasswordSubmit}>
              <div className="form-group">
                <label>New Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPasswordText ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter at least 6 characters"
                    minLength={6}
                    required
                    style={{ width: '100%', paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordText(!showPasswordText)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                    title={showPasswordText ? 'Hide password' : 'Show password'}
                  >
                    {showPasswordText ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password *</label>
                <input
                  type={showPasswordText ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  minLength={6}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  onClick={() => {
                    const randomPass = Math.random().toString(36).slice(-8) + 'A1!';
                    setNewPassword(randomPass);
                    setConfirmPassword(randomPass);
                    setShowPasswordText(true);
                  }}
                  style={{ fontSize: 12, padding: '4px 8px' }}
                >
                  ⚡ Auto-generate secure password
                </button>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={resettingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={resettingPassword || !newPassword}
                >
                  {resettingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
